import {collection,doc,getDoc,getDocs,runTransaction,setDoc,deleteDoc} from 'firebase/firestore';
import {db} from '../../Security/firebase-config';
import {gatherUserTeamIds} from '../Calendar/calendarSubscriptions';
import {memberIds} from '../homework/homeworkModel';
import {hasTeamFeature,resolveTeamEntitlements} from '../../utils/entitlements';
import {availabilityWeek} from '../../shared/availability';
import {availabilityImportPlan} from '../Calendar/availabilityImport';

export async function loadAvailabilityTeam(teamId,uid) {
  const [team,entitlements,subscription]=await Promise.all([getDoc(doc(db,'teams',teamId)),getDoc(doc(db,'teams',teamId,'accessV2','entitlements')).catch(()=>null),getDoc(doc(db,'teams',teamId,'billingV2','subscription')).catch(()=>null)]);
  if(!team.exists()||!memberIds(team.data()).includes(uid))throw new Error('You are no longer a member of this team.');
  const data=team.data(),access=resolveTeamEntitlements(entitlements?.exists()?entitlements.data():subscription?.exists()?subscription.data():null);
  return {teamId,name:data.teamName||data.name||teamId,timeZone:data.teamTimeZone||'UTC',enabled:hasTeamFeature(access,'teamCalendar'),data};
}

export async function previewAvailabilityImports({uid,weekKey,teamIds}) {
  const profile=await getDoc(doc(db,'users',uid));
  if(!profile.exists())throw new Error('Your account could not be loaded.');
  const allowed=new Set(gatherUserTeamIds(profile.data()));
  const personal=(await getDocs(collection(db,'users',uid,'personalAvailabilities'))).docs.map(item=>({id:item.id,...item.data()}));
  const targets=teamIds||[...allowed];
  return Promise.all(targets.map(async teamId=>{
    try {
      if(!allowed.has(teamId))throw new Error('This team is no longer on your account.');
      const team=await loadAvailabilityTeam(teamId,uid);
      const existing=(await getDocs(collection(db,'teams',teamId,'availabilities'))).docs.map(item=>({id:item.id,...item.data()}));
      const plan=availabilityImportPlan({personal,existing,uid,weekKey,timeZone:team.timeZone});
      return {...team,...plan,...(!team.enabled?{blocks:[],reason:'Availability editing requires Basic or higher',disabled:true}:{})};
    }catch(error){return {teamId,name:teamId,blocks:[],disabled:true,reason:error.message||'Could not load this team'};}
  }));
}

export async function importAvailabilityToTeams({uid,username,weekKey,teamIds}) {
  // Refresh membership, entitlements and both calendars at confirmation time.
  const rows=await previewAvailabilityImports({uid,weekKey,teamIds});
  const safeName=typeof username==='string'&&!username.includes('@')?username:'Member';
  return Promise.all(rows.map(async row=>{
    if(row.disabled||!row.blocks.length)return {...row,ok:!row.disabled,added:0};
    let added=0;
    try {
      for(let offset=0;offset<row.blocks.length;offset+=350){
        const chunk=row.blocks.slice(offset,offset+350).map(block=>({...block,ref:doc(db,'teams',row.teamId,'availabilities',`personal_${uid}_${weekKey}_${Date.parse(block.startUTC)}_${Date.parse(block.endUTC)}`)}));
        added+=await runTransaction(db,async transaction=>{
          const snapshots=await Promise.all(chunk.map(block=>transaction.get(block.ref)));
          let count=0;
          chunk.forEach((block,index)=>{if(snapshots[index].exists())return;count++;
            transaction.set(block.ref,{userId:uid,username:safeName,startUTC:block.startUTC,endUTC:block.endUTC,repeat:'none',repeatEndDate:null,exceptions:[],overrides:{},scope:'imported-from-personal',sourceWeekKey:weekKey,sourceUid:uid,sourcePersonalAvailId:null,importedAt:Date.now(),createdAt:Date.now()});
          });
          return count;
        });
      }
      return {...row,ok:true,added};
    }catch(error){return {...row,ok:false,added,reason:error.message||'Import failed. Please retry.'};}
  }));
}

// A week declaration overrides recurring availability for this team only.
export async function setTeamUnavailableForWeek({teamId,uid,username,weekKey,unavailable}) {
  if(!uid||!teamId)throw new Error('Select a team before updating availability.');
  const team=await loadAvailabilityTeam(teamId,uid);
  if(!team.enabled)throw new Error('Availability editing requires Basic or higher.');
  const range=availabilityWeek(weekKey,team.timeZone);
  if(range.weekKey!==weekKey)throw new Error('Choose a valid availability week.');
  const target=doc(db,'teams',teamId,'availabilities',`unavailable_${uid}_${weekKey}`);
  if(!unavailable){await deleteDoc(target);return;}
  await setDoc(target,{userId:uid,username:username&&!username.includes('@')?username:'Member',unavailableAllWeek:true,submissionWeekKey:weekKey,scope:'team-unavailable-all-week',startUTC:range.start.toISOString(),endUTC:new Date(+range.end-1).toISOString(),repeat:'none',repeatEndDate:null,exceptions:[],overrides:{},createdAt:Date.now(),updatedAt:Date.now()},{merge:true});
}
